import React, { useState } from 'react';
import { NCZIPatientSummaryContactInformation } from '../Interfaces/ncziPatientInformations';
import { NCZIEhrExtractEnvelope } from '../Interfaces/ncziTypes';
import { ElectronicDPNFilterValues } from '../Components/ElectronicDPN/Filter/ElectronicDPNFilter';
import { ElectronicExaminationFilterValues } from '../Components/ElectronicExamination/Filter/ElectronicExaminationFilter';
import { ElectronicVaccinationFilterValues } from '../Components/ElectronicVaccination/Filter/ElectronicVaccinationFilter';
import { NCZIElectronicPrescriptionItem } from '../Interfaces/ncziElectronicPrescriptionItem';
import { NCZIElectronicPrescriptionMedicationItem } from '../Interfaces/ncziElectronicPrescriptionMedicationItem';

export interface NCZIContextProps {
	patientBirthNumber?: string;
	setPatientBirthNumber: (newState: string) => void;
	patientMeddiInsurance?: string;
	setPatientMeddiInsurance: (newState: string | null) => void;
	patientInsurance?: string | null;
	setPatientInsurance: (newState: string) => void;
	patientInsuranceNonPayer?: boolean;
	setPatientInsuranceNonPayer: (newState: boolean) => void;
	deferredZsEligibility?: boolean;
	setDeferredZsEligibility: (newState: boolean) => void;
	patientIdentifier?: string;
	patientIdentifierLoadingCommandId?: string;
	setPatientIdentifierLoadingCommandId: (newState: string) => void;
	patientSummaryContactData?: NCZIPatientSummaryContactInformation;
	setPatientSummaryContactData: (newState: any) => void;
	setPatientIdentifier: (newState: string) => void;
	patientSummaryData?: NCZIEhrExtractEnvelope;
	setPatientSummaryData: (newState: any) => void;
	patientExaminationData?: NCZIEhrExtractEnvelope;
	setPatientExaminationData: (newState: any) => void;
	patientExaminationPage?: number;
	setPatientExaminationPage: (newState: number) => void;
	patientExaminationFilter?: ElectronicExaminationFilterValues;
	setPatientExaminationFilter: (
		newState: ElectronicExaminationFilterValues,
	) => void;
	patientVaccinationData?: NCZIEhrExtractEnvelope;
	setPatientVaccinationData: (newState: any) => void;
	patientVaccinationPage?: number;
	setPatientVaccinationPage: (newState: number) => void;
	patientVaccinationFilter?: ElectronicVaccinationFilterValues;
	setPatientVaccinationFilter: (
		newState: ElectronicVaccinationFilterValues,
	) => void;
	patientDPNData?: NCZIEhrExtractEnvelope;
	setPatientDPNData: (newState: any) => void;
	patientDPNPage?: number;
	setPatientDPNPage: (newState: number) => void;
	patientDPNFilter?: ElectronicDPNFilterValues;
	setPatientDPNFilter: (newState: ElectronicDPNFilterValues) => void;
	patientElectronicPrescriptionData?: NCZIElectronicPrescriptionItem[];
	setPatientElectronicPrescriptionData: (newState: any) => void;
	patientElectronicPrescriptionMedicationData?: NCZIElectronicPrescriptionMedicationItem[];
	setPatientElectronicPrescriptionMedicationData: (newState: any) => void;
}

export interface NCZIContextProviderProps {
	values?: NCZIContextProps;
	children?: React.ReactChild | React.ReactChild[];
}

export const NCZIContext = React.createContext<NCZIContextProps>({
	patientBirthNumber: undefined,
	setPatientBirthNumber: () => {},
	patientMeddiInsurance: undefined,
	setPatientMeddiInsurance: () => {},
	patientInsurance: undefined,
	setPatientInsurance: () => {},
	patientInsuranceNonPayer: undefined,
	setPatientInsuranceNonPayer: () => {},
	deferredZsEligibility: undefined,
	setDeferredZsEligibility: () => {},
	patientSummaryContactData: undefined,
	setPatientSummaryContactData: () => {},
	patientIdentifier: undefined,
	setPatientIdentifier: () => {},
	patientSummaryData: undefined,
	setPatientSummaryData: () => {},
	patientExaminationData: undefined,
	setPatientExaminationData: () => {},
	patientExaminationPage: undefined,
	setPatientExaminationPage: () => {},
	patientExaminationFilter: undefined,
	setPatientExaminationFilter: () => {},
	patientVaccinationData: undefined,
	setPatientVaccinationData: () => {},
	patientVaccinationPage: undefined,
	setPatientVaccinationPage: () => {},
	patientVaccinationFilter: undefined,
	setPatientVaccinationFilter: () => {},
	patientDPNData: undefined,
	setPatientDPNData: () => {},
	patientDPNPage: undefined,
	setPatientDPNPage: () => {},
	patientDPNFilter: undefined,
	setPatientDPNFilter: () => {},
	patientIdentifierLoadingCommandId: undefined,
	setPatientIdentifierLoadingCommandId: () => {},
	patientElectronicPrescriptionData: undefined,
	setPatientElectronicPrescriptionData: () => {},
	patientElectronicPrescriptionMedicationData: undefined,
	setPatientElectronicPrescriptionMedicationData: () => {},
});

export const useNCZIContext: () => NCZIContextProps = () =>
	React.useContext<NCZIContextProps>(NCZIContext);

export function NCZIContextProvider({
	values,
	children,
}: NCZIContextProviderProps) {
	const [patientBirthNumber, setPatientBirthNumber] = useState(
		values?.patientBirthNumber,
	);
	const [patientInsurance, setPatientInsurance] = useState(
		values?.patientInsurance,
	);
	const [patientMeddiInsurance, setPatientMeddiInsurance] = useState(
		values?.patientMeddiInsurance,
	);
	const [patientIdentifier, setPatientIdentifier] = useState(
		values?.patientIdentifier,
	);
	const [patientInsuranceNonPayer, setPatientInsuranceNonPayer] = useState(
		values?.patientInsuranceNonPayer,
	);
	const [deferredZsEligibility, setDeferredZsEligibility] = useState(
		values?.deferredZsEligibility,
	);
	const [
		patientIdentifierLoadingCommandId,
		setPatientIdentifierLoadingCommandId,
	] = useState(values?.patientIdentifierLoadingCommandId);
	const [patientSummaryContactData, setPatientSummaryContactData] = useState(
		values?.patientSummaryContactData,
	);
	const [patientSummaryData, setPatientSummaryData] = useState(
		values?.patientSummaryData,
	);
	const [patientExaminationData, setPatientExaminationData] = useState(
		values?.patientExaminationData,
	);
	const [patientExaminationPage, setPatientExaminationPage] = useState(
		values?.patientExaminationPage,
	);
	const [patientExaminationFilter, setPatientExaminationFilter] = useState(
		values?.patientExaminationFilter,
	);
	const [patientVaccinationData, setPatientVaccinationData] = useState(
		values?.patientVaccinationData,
	);
	const [patientVaccinationPage, setPatientVaccinationPage] = useState(
		values?.patientVaccinationPage,
	);
	const [patientVaccinationFilter, setPatientVaccinationFilter] = useState(
		values?.patientVaccinationFilter,
	);
	const [patientDPNData, setPatientDPNData] = useState(values?.patientDPNData);
	const [patientDPNPage, setPatientDPNPage] = useState(values?.patientDPNPage);
	const [patientDPNFilter, setPatientDPNFilter] = useState(
		values?.patientDPNFilter,
	);
	const [
		patientElectronicPrescriptionData,
		setPatientElectronicPrescriptionData,
	] = useState(values?.patientElectronicPrescriptionData);
	const [
		patientElectronicPrescriptionMedicationData,
		setPatientElectronicPrescriptionMedicationData,
	] = useState(values?.patientElectronicPrescriptionMedicationData);

	return (
		<NCZIContext.Provider
			value={{
				patientBirthNumber,
				setPatientBirthNumber,
				patientMeddiInsurance,
				setPatientMeddiInsurance,
				patientInsurance,
				setPatientInsurance,
				patientInsuranceNonPayer,
				setPatientInsuranceNonPayer,
				deferredZsEligibility,
				setDeferredZsEligibility,
				patientIdentifier,
				setPatientIdentifier,
				patientIdentifierLoadingCommandId,
				setPatientIdentifierLoadingCommandId,
				patientSummaryContactData,
				setPatientSummaryContactData,
				patientSummaryData,
				setPatientSummaryData,
				patientExaminationData,
				setPatientExaminationData,
				patientExaminationPage,
				setPatientExaminationPage,
				patientExaminationFilter,
				setPatientExaminationFilter,
				patientVaccinationData,
				setPatientVaccinationData,
				patientVaccinationPage,
				setPatientVaccinationPage,
				patientVaccinationFilter,
				setPatientVaccinationFilter,
				patientDPNData,
				setPatientDPNData,
				patientDPNPage,
				setPatientDPNPage,
				patientDPNFilter,
				setPatientDPNFilter,
				patientElectronicPrescriptionData,
				setPatientElectronicPrescriptionData,
				patientElectronicPrescriptionMedicationData,
				setPatientElectronicPrescriptionMedicationData,
			}}>
			{children}
		</NCZIContext.Provider>
	);
}
